/**
 * Les textes de l’interface vivent ici ; ceux du noyau dans core-fr.ts. Aucune chaîne en dur dans le code —
 * la règle vaut dès le premier commit parce que la rétrofitter coûte plusieurs
 * fois son écriture initiale (CLAUDE.md §7).
 */
export const fr = {
  datasetDuplicateColumn: (name: string): string => `Colonne en double : « ${name} ».`,
  datasetPositiveUncertainty: (name: string): string => `L'incertitude « ${name} » doit être strictement positive.`,
  datasetInvalidCell: (name: string, reason: string): string => `Valeur invalide dans « ${name} » : ${reason}`,
  datasetTooLarge: "Le fichier dépasse la limite de 2 Mo. Importez un extrait plus petit.",
  datasetTooManyRows: (limit: number): string => `Le fichier dépasse la limite de ${limit} lignes. Importez un extrait plus petit.`,
  datasetTooManyColumns: (limit: number): string => `Le fichier dépasse la limite de ${limit} colonnes.`,
  comparisonInvalidRow: (line: number): string => `Relevé ${line} invalide : vérifiez l'instant UTC, la latitude, la longitude, les longueurs et une incertitude strictement positive.`,
  datasetProcessingFailed: "Le fichier a été lu, mais la comparaison n'a pas pu être affichée.",
  appTitle: "Verlet",
  mobileSpeedValue: (steps: number): string => steps <= 10 ? "Très lent" : steps < 40 ? "Lent" : steps <= 80 ? "Normal" : steps <= 160 ? "Rapide" : "Très rapide",
  mobileTools: "Créer et manipuler",
  mobileBodies: "Corps du système",
  mobileInspector: "Modifier le corps",
  mobileClose: "Fermer",
  mobileAdd: "Ajouter",
  mobileList: "Corps",
  mobileEdit: "Modifier",
  mobileNavigation: "Commandes du laboratoire",
  mobileHint: "Glissez pour tourner · Pincez pour zoomer",
  mobilePlace: "Touchez pour placer le corps, puis dessinez sa vitesse. Validez quand la flèche vous convient.",
  mobileMove: "Touchez la nouvelle position, puis validez le déplacement.",
  mobileLaunch: "Dessinez la vitesse depuis le corps, puis validez le lancer.",
  mobileConfirm: "Valider",
  mobileCancel: "Annuler",
  mobileSelected: "Corps sélectionné",
  mobileBack: "Retour au monde",

  themeLight: "Thème clair",
  themeDark: "Thème sombre",
  sandboxTagline: "Votre laboratoire spatial",
  toolSelect: "Explorer",
  toolPlanet: "+ Planète",
  toolStar: "+ Étoile",
  toolMove: "Déplacer",
  toolVelocity: "Lancer",
  toolCancel: "Annuler · Échap",
  toolReady: "Ajoutez un corps, changez son mouvement, regardez le système évoluer.",
  toolPlaceHint: "Simulation en pause. Cliquez pour placer le corps, puis cliquez dans la direction où le lancer. Au clavier : flèches et Entrée. Échap pour annuler.",
  toolLaunchHint: "Choisissez la direction et la vitesse avec la flèche, puis cliquez pour valider. Échap pour annuler.",
  toolMoveHint: "Simulation en pause. Cliquez dans le plan orbital pour déplacer le corps sélectionné. Échap pour annuler.",
  toolDone: "Corps modifié. Reprenez le temps pour observer sa trajectoire.",
  toolNeedSelection: "Sélectionnez d’abord un corps dans la scène ou dans la liste.",
  toolUnavailable: "La manipulation dans la scène demande une vue 3D disponible. Les réglages du panneau restent utilisables.",
  toolSpeed: (speed: string): string => `Vitesse de lancement : ${speed} km/s`,
  sandboxAbout: "À propos de la simulation",
  sandboxLimits: "Gravitation dans un plan, vue en 3D. Les corps se traversent : les collisions et la fragmentation ne sont pas encore simulées. Les tailles sont amplifiées à distance pour rester visibles.",
  sandboxTools: "Outils du laboratoire spatial",
  sandboxReframe: "Vue d’ensemble",
  sandboxPanels: "Panneaux",
  velocityTitle: "Mouvement",
  velocitySpeed: "Vitesse (km/s)",
  velocityAngle: "Direction (°)",
  velocityApply: "Appliquer",
  velocityStop: "Immobiliser",
  velocityHint: "Dans le plan orbital : 0° vers +X, 90° vers +Y. Appliquer met le temps en pause.",

  // ── Ératosthène ──────────────────────────────────────────────────────────
  eratosthenesLabel: "Ératosthène",
  eratosthenesQuestion: "La Terre est-elle plate ?",
  eratosthenesLede:
    "Deux modèles prédisent la longueur de l'ombre d'un bâton. Seule une mesure réelle peut les départager — et le logiciel refuse de trancher sans elle.",
  sectionSituation: "Où et quand",
  sectionSchematic: "Les deux géométries en présence",
  sectionFlatControls: "Les deux réglages du modèle plat",

  // ── Chute libre ──────────────────────────────────────────────────────────
  fallLabel: "Chute libre",
  fallQuestion: "Une pierre tombe-t-elle plus vite qu'un caillou ?",
  fallLede:
    "Aristote fait tomber les corps lourds plus vite ; Galilée les fait tomber ensemble. Faites glisser la masse : une seule des deux courbes bouge.",
  sectionFallSituation: "Ce qu'on lâche, et d'où",
  sectionAristotleControls: "Le réglage qu'Aristote réclame",
  controlFallHeight: "Hauteur de lâcher",
  controlFallHeightHint:
    "Plus la chute est longue, plus l'écart entre les deux modèles a le temps de se creuser — et plus l'air, qu'aucun des deux ne modélise, fausse la mesure réelle.",
  controlFallMass: "Masse du corps",
  controlFallMassHint:
    "C'est le curseur qui porte l'expérience. Chez Galilée il ne change rien du tout ; chez Aristote il change tout.",
  controlFallGravity: "Pesanteur de surface",
  controlFallGravityHint:
    "Les boutons ci-dessus posent les valeurs publiées pour quelques corps du système solaire. Le curseur va au-delà : une pesanteur qui n'existe nulle part reste un réglage licite.",
  controlReferenceSpeed: "Vitesse de chute d'une masse de référence",
  controlReferenceSpeedHint:
    "L'unique hypothèse libre d'Aristote : la vitesse qu'atteint un kilogramme dans ce milieu. Aucune théorie ne la fixe, et le logiciel ne la calcule jamais.",
  bodiesLabel: "Se poser sur un corps du système solaire",
  fallSituationCaveat:
    "Réglez ce que vous voulez, posez-vous où vous voulez. Tout ce qui s'affiche ici est une conséquence des modèles — aucune mesure n'est représentée, et rien n'est départagé. Explorer ne demande aucune donnée ; trancher, si.",
  aristotleControlsCaveat:
    "À vous de régler. Le logiciel ne cherche jamais la vitesse qui ferait coller Aristote à Galilée — et de toute façon aucune ne les fait coïncider sur deux masses à la fois. Galilée n'a aucun curseur : c'est précisément ce qui se compte.",
  fallChartTitle: "La hauteur du corps au fil de la chute, selon chaque modèle",
  fallChartDescription: (mass: string): string =>
    `Hauteur au-dessus du sol en fonction du temps écoulé depuis le lâcher, pour une masse de ${mass} kg, telle que la prédit chaque modèle. Les deux courbes n'atteignent pas le sol au même instant. Aucune mesure n'est représentée.`,
  axisFallHeight: "Hauteur au-dessus du sol (m)",
  fallReadoutCaption:
    "Ce que chaque modèle prédit au réglage courant. Deux prédictions qui diffèrent ne disent pas laquelle a raison.",
  columnFallTime: "Durée de chute",
  columnImpactSpeed: "Vitesse à l'impact",
  noFallTitle: "Aucune chute à tracer.",
  noFallExplanation:
    "Aucun des deux modèles ne prédit de chute avec ces réglages. Ce n'est pas une panne : un modèle a le droit de ne rien dire, et c'est plus honnête qu'une courbe inventée pour remplir le cadre.",
  // La vue animée de la chute
  sectionFallScene: "La chute, en temps réel",
  galileoShort: "Galilée",
  aristotleShort: "Aristote",
  fallPlay: "Lecture",
  fallPause: "Pause",
  fallReplay: "Rejouer",
  fallTimeLabel: "Temps écoulé",
  fallSceneSide: (rank: number, count: number): string => {
    if (count <= 1) return "Au centre";
    if (rank === 0) return "À gauche";
    if (rank === count - 1) return "À droite";
    return "Au centre";
  },
  fallSceneAlt:
    "Vue en trois dimensions : deux billes lâchées ensemble de la même hauteur, chacune descendant le long de son rail, au-dessus d'un sol quadrillé. Celle de gauche suit la prédiction de Galilée, celle de droite celle d'Aristote. Les mêmes hauteurs, aux mêmes instants, sont lisibles dans le graphe et dans la table qui suivent.",
  fallSceneCaveat:
    "Les deux billes partent du même point, à la même hauteur, au même instant : tout ce qui les sépare à l'écran vient des modèles. La hauteur de lâcher vaut la hauteur réglée, et les positions sont dans ce rapport-là — ce que vous voyez, la table le chiffre. Le temps est une entrée, pas une horloge : reculez le curseur et la chute rejoue exactement les mêmes positions. Rien n'est départagé ici pour autant — voir une bille toucher le sol avant l'autre montre un désaccord entre deux modèles, pas lequel décrit le monde. Il y faudrait deux vrais objets et un chronomètre.",

  // ── Orbite ───────────────────────────────────────────────────────────────
  orbitLabel: "Orbite",
  orbitPresetsLabel: "Aller à une configuration remarquable",
  presetNewtonExponent: "Exposant deux — l'orbite se referme",
  presetCircularOrbit: "Orbite circulaire",
  presetRosette: "Rosace franche",
  orbitQuestion: "Pourquoi le carré de la distance ?",
  orbitLede:
    "Sur un tour, presque n'importe quelle loi d'attraction dessine une ellipse. Sur dix, une seule referme l'orbite sur elle-même. Poussez l'exposant et regardez le sillage.",
  sectionOrbitSituation: "L'étoile et le corps qui l'orbite",
  sectionOrbitScene: "L'orbite, en mouvement",
  sectionExponentControl: "Le réglage que la loi libre réclame",
  controlCentralMass: "Masse de l'étoile",
  controlCentralMassHint:
    "Elle fixe l'intensité de l'attraction, donc la période. Elle ne change rien à la fermeture de l'orbite — c'est bien pour ça qu'elle n'est pas ce qu'on regarde ici.",
  controlOrbitDistance: "Distance de départ",
  controlOrbitDistanceHint:
    "Le corps part de là, perpendiculairement à la ligne qui le joint à l'étoile. C'est aussi la distance à laquelle les deux lois s'accordent exactement.",
  controlOrbitSpeed: "Vitesse de départ, en fraction de la vitesse circulaire",
  controlOrbitSpeedHint:
    "À un, l'orbite est un cercle et il n'y a pas de grand axe à faire tourner. En dessous, elle s'allonge — et c'est l'ellipse qui rend la précession visible.",
  controlExponent: "Exposant de décroissance",
  controlExponentHint:
    "L'unique hypothèse libre. À deux, cette loi est exactement celle de Newton. Ailleurs, l'orbite cesse de se refermer.",
  orbitSituationCaveat:
    "Réglez ce que vous voulez. Tout ce qui s'affiche ici est une conséquence des modèles — aucune mesure n'est représentée, et rien n'est départagé. Explorer ne demande aucune donnée ; trancher, si.",
  exponentControlCaveat:
    "À vous de régler. Le logiciel ne cherche jamais l'exposant qui ferait coller le modèle à des données. Newton n'a aucun curseur : son exposant vaut deux parce que sa théorie le dit, et c'est précisément ce qui se compte.",
  orbitSceneAlt:
    "Vue en trois dimensions du plan d'une orbite, regardé de biais : une étoile au centre, et un corps qui tourne autour en laissant derrière lui la trace de son passage. Selon la loi d'attraction retenue, cette trace repasse sur elle-même ou dessine une rosace. Les mêmes grandeurs sont lisibles dans la table qui suit.",
  orbitSceneCaveat:
    "Les distances sont dans le rapport exact des distances calculées ; l'étoile, elle, est démesurée — à l'échelle, elle serait un point invisible. Ce qu'il faut regarder n'est pas la forme d'un tour mais ce que devient le sillage au bout de plusieurs : une orbite qui se referme, ou une rosace. Le temps est une entrée, pas une horloge : reculez le curseur et le mouvement rejoue exactement les mêmes positions. Rien n'est départagé ici pour autant — une orbite qui se referme montre ce que fait cette loi-là, pas qu'elle décrit le monde.",
  orbitChartTitle: "La distance à l'étoile au fil du temps, selon chaque loi",
  orbitChartDescription: (exponent: string): string =>
    `Distance entre le corps et l'étoile en fonction du temps, pour un exposant réglé à ${exponent}, telle que la prédit chaque loi. Les creux et les bosses sont les passages au plus près et au plus loin. Aucune mesure n'est représentée.`,
  axisOrbitRadius: "Distance à l'étoile (unités de départ)",
  orbitReadoutCaption:
    "Ce que chaque loi prédit au réglage courant. Deux prédictions qui diffèrent ne disent pas laquelle a raison.",
  columnPeriod: "Durée d'un tour",
  columnPrecession: "Rotation du grand axe, par tour",
  orbitClosed: "L'orbite se referme",
  orbitNotMeasured: "Pas encore mesurable",
  noOrbitTitle: "Aucune orbite à tracer.",
  noOrbitExplanation:
    "Aucune des deux lois ne produit de trajectoire exploitable avec ces réglages. Ce n'est pas une panne : un modèle a le droit de ne rien dire, et c'est plus honnête qu'une courbe inventée pour remplir le cadre.",


  noDatasetForExperiment:
    "Aucun relevé n'est chargé pour cette expérience. La confrontation ne peut donc pas s'exécuter : les mesures viennent du dehors, elles ne se fabriquent pas ici.",

  // Situation — les données du problème, communes aux deux modèles
  controlLatitude: "Latitude",
  controlLatitudeHint:
    "D'un pôle à l'autre. Les hautes latitudes sont les plus instructives : au-delà du cercle polaire, le Soleil finit par ne plus se lever du tout.",
  controlLongitude: "Longitude",
  controlLongitudeHint:
    "Elle ne change pas la longueur de l'ombre à midi solaire, seulement l'heure UTC à laquelle ce midi tombe.",
  controlDay: "Jour de l'année",
  controlDayHint:
    "La déclinaison du Soleil suit la date. C'est en balayant l'année que les deux modèles s'écartent le plus.",
  controlGnomon: "Hauteur du bâton",
  controlGnomonHint:
    "L'ombre lui est proportionnelle : c'est le rapport ombre / hauteur qui porte la géométrie, pas la longueur brute.",
  placesLabel: "Aller à un lieu remarquable",
  explorationCaveat:
    "Réglez ce que vous voulez, allez où vous voulez. Tout ce qui s'affiche ici est une conséquence des modèles — aucune mesure n'est représentée, et rien n'est départagé. Explorer ne demande aucune donnée ; trancher, si.",
  sectionComparison: "Confrontation aux mesures",
  sectionMeasurements: "Mesures externes",
  sectionPredictions: "Ce que prédisent les modèles",
  sectionTable: "Détail chiffré",

  // Schéma 3D
  schematicSpherical: "Terre sphérique, Soleil lointain",
  schematicFlat: "Plan horizontal, Soleil local",
  schematicToggleLabel: "Géométrie représentée",
  schematicAlt: (geometry: "spherical" | "flat"): string =>
    geometry === "spherical"
      ? "Schéma : deux bâtons plantés perpendiculairement à une sphère, éclairés par des rayons parallèles. Celui qui est sous le Soleil ne porte pas d'ombre, l'autre si."
      : "Schéma : deux bâtons plantés verticalement sur un disque, éclairés par un Soleil proche dont les rayons divergent. Celui qui est sous le Soleil ne porte pas d'ombre, l'autre si.",
  schematicCaveat:
    "Le bâton est démesuré par rapport à la Terre — à l'échelle, l'ombre d'un mètre serait invisible — et ni les distances ni la taille du Soleil ne sont respectées. En revanche le rapport entre l'ombre et le bâton est exactement celui que le modèle sélectionné prédit : ce dessin dit la même chose que les chiffres, autrement. Le second bâton marque le point où le Soleil est au zénith, celui qui ne porte aucune ombre. Aucune mesure n'est représentée, et les deux géométries rendent compte des mêmes observations — c'est bien pour ça qu'un dessin ne tranche rien.",
  schematicLoading: "Chargement de la vue…",
  schematicUnavailable:
    "La vue 3D n'a pas pu s'afficher — WebGL est indisponible. Ce n'est qu'une illustration : rien de ce que dit cette page n'en dépend.",

  // Graphe
  chartTitle: "Longueur d'ombre : mesures et prédictions des modèles",
  chartDescription: (count: number): string =>
    `${count} relevé${count > 1 ? "s" : ""} d'ombre, comparés aux prédictions de chaque modèle. Écart moyen —`,
  axisShadowLength: "Longueur d'ombre (m)",
  seriesMeasured: "Mesures",
  noPlottableObservation: "Aucun relevé exploitable à tracer.",
  noShadowTitle: "Aucune ombre à midi, toute l'année.",
  noShadowExplanation:
    "À cette latitude, le Soleil ne passe jamais au-dessus de l'horizon à l'instant considéré : il n'y a pas d'ombre à mesurer, et les modèles n'en prédisent aucune. Ce n'est pas une panne — c'est ce que dit la nuit polaire. Rapprochez-vous de l'équateur, ou changez de date.",

  // Table
  measurementsCaption: "Relevés, prédictions et écarts",
  summaryCaption: "Bilan par modèle",
  columnDate: "Date",
  columnSite: "Lieu",
  columnMeasured: "Mesure",
  columnGap: "Écart",
  columnModel: "Modèle",
  columnMeanGap: "Écart moyen",
  columnFreeHypotheses: "Hypothèses libres",
  noPrediction: "Pas de prédiction",
  notComputed: "Non calculé",
  perObservation: "1 par relevé",

  // Lecture chiffrée au réglage courant
  readoutCaption:
    "Ce que chaque modèle prédit au réglage courant. Deux prédictions qui diffèrent ne disent pas laquelle a raison.",
  columnShadowLength: "Longueur d'ombre",
  columnShadowRatio: "Ombre / bâton",

  // Prédictions seules
  predictionsTitle: "Ce que chaque modèle prédit, sur une année",
  predictionsDescription: (year: string): string =>
    `Longueur de l'ombre d'un gnomon d'un mètre à midi solaire au fil de l'année ${year}, telle que la prédit chaque modèle. Les graduations de l'axe horizontal portent le jour et le mois. Aucune mesure n'est représentée.`,
  predictionsOnlyCaveat:
    "Aucune mesure n'est chargée : ces courbes sont des conséquences de modèles, pas des observations. Rien n'est départagé ici — il y faudrait des relevés réels.",
  predictionsWithMeasurementsCaveat:
    "Ces courbes annuelles restent des prédictions. Les relevés importés ne sont pas fondus dedans : ils apparaissent plus bas comme des points avec leurs barres d'incertitude, dans la confrontation.",

  // Import des mesures
  datasetHelp:
    "Chargez un CSV produit hors du logiciel. Chaque relevé doit donner le lieu, l'instant UTC, la position, la hauteur du gnomon, la longueur d'ombre et son incertitude. La provenance complète est obligatoire ; le fichier ne quitte pas ce navigateur.",
  datasetChoose: "Fichier CSV",
  datasetTemplate: "Télécharger le modèle",
  datasetClear: "Retirer les mesures",
  datasetEmpty:
    "Aucun relevé chargé. Le comparateur restera verrouillé jusqu'à l'import d'un fichier externe complet.",
  datasetRejected: (fileName: string): string => `« ${fileName} » a été refusé`,
  datasetLoaded: (fileName: string): string => `« ${fileName} » est chargé pour la confrontation`,
  datasetAuthenticityCaveat:
    "Le logiciel valide le format et la provenance déclarée ; il ne peut pas certifier que les valeurs ont réellement été mesurées.",
  datasetUnreadable: "Le navigateur n'a pas pu lire ce fichier.",
  datasetLine: (line: number): string => `ligne ${line}`,
  datasetName: "Jeu de mesures",
  datasetSource: "Source",
  datasetMethod: "Acquisition",
  datasetPublishedOn: "Publication",
  datasetLicense: "Licence",
  datasetObservationCount: "Relevés",
  datasetRows: (count: number): string => `${count} relevé${count > 1 ? "s" : ""}`,

  // Réglages du modèle plat
  flatSunHeightLabel: "Hauteur du Soleil au-dessus du plan",
  flatSunHeightHint:
    "Première hypothèse libre. Le modèle plat ne la déduit de rien : il faut la lui donner.",
  flatScaleLabel: "Échelle de la carte plate",
  flatScaleHint:
    "Seconde hypothèse libre : la distance parcourue sur le plan quand la latitude change d'un degré.",
  flatControlsCaveat:
    "À vous de régler. Le logiciel ne cherche jamais les valeurs qui feraient coller un modèle aux mesures — il affiche l'écart, vous en tirez les conclusions. Le modèle sphérique n'a aucun curseur : c'est précisément ce qui se compte.",

  // Refus
  comparisonRefused: "La comparaison ne peut pas s'exécuter.",
  contentInvalid: "Le contenu de l'expérience n'est pas valide.",

  // ── Le monde ─────────────────────────────────────────────────────────────
  worldStar: "Étoile",
  worldPlanet: "Planète",
  worldIntruder: "Étoile de passage",
  worldSceneAlt:
    "Vue en trois dimensions interactive d'un système de corps célestes en mouvement. À la souris, faites glisser pour tourner, utilisez la molette pour approcher, cliquez pour sélectionner et double-cliquez pour aller voir un corps. Au clavier, utilisez les flèches pour tourner, plus et moins pour zoomer, et Origine pour recadrer.",
  worldSceneRole: "vue 3D interactive",
  worldUnavailable:
    "La vue 3D n'a pas pu démarrer : ce navigateur n'expose pas WebGL. Tout le reste de l'application fonctionne.",
  worldLoading: "Chargement de la vue…",
  worldCaveat:
    "Distances et tailles sont fidèles : une planète a le rayon qu'elle a, dans les mêmes unités que son orbite. Un corps trop petit pour être vu garde une taille minimale à l'écran — ce n'est alors plus une taille mais un repère de position, et approcher lui rend la sienne. Double-cliquez un corps pour aller le voir.",

  scenariosLabel: "Scénarios",
  scenarioSystemLabel: "Système",
  scenarioSystemSummary: "Une étoile, trois planètes sur des orbites circulaires. Le cas calme.",
  scenarioBinaryLabel: "Étoile double",
  scenarioBinarySummary:
    "Deux étoiles autour de leur barycentre, et une planète qui les entoure toutes les deux.",
  scenarioLagrangeLabel: "Triangle",
  scenarioLagrangeSummary:
    "Trois masses égales en triangle équilatéral. La configuration est instable : elle se disloque.",
  scenarioEncounterLabel: "Rencontre",
  scenarioEncounterSummary:
    "Une étoile traverse un système établi. Des planètes seront éjectées — impossible de dire lesquelles à l'avance.",
  scenarioLoneLabel: "Étoile seule",
  scenarioLoneSummary: "Une étoile, et rien d'autre. À vous de poser le reste.",

  worldNoComparison:
    "Ce monde ne confronte rien, et il ne le peut pas : il montre les conséquences de la loi qu'on lui a donnée, ce qui ne dit rien de ce que fait le monde réel. Départager demanderait des mesures que ce logiciel n'a pas produites.",
  worldFeaturedBridge: "Une analyse s'y prête, elle, à partir de relevés que vous chargez :",

  // Barre et commandes
  worldEntitiesTitle: "Dans le monde",
  worldScaleDetails: "À propos des échelles",
  worldInspectorTitle: "Inspecteur",
  worldNoSelection: "Aucun corps sélectionné. Cliquez-en un dans la vue, ou dans la liste.",
  worldPlay: "Reprendre",
  worldPause: "Suspendre",
  worldSpeedLabel: "Vitesse",
  worldSpeedValue: (steps: number): string => `${steps} pas par image`,
  worldSpeedHint: "Nombre de pas d'une heure exécutés par image. Le pas ne change jamais de taille.",
  worldElapsedLabel: "Temps simulé",
  worldDriftLabel: "Dérive d'énergie",
  worldDriftHint:
    "Écart relatif de l'énergie totale depuis le départ. Un schéma symplectique la fait osciller autour de zéro ; il ne la fait pas croître.",
  worldFollowLabel: "Y aller",
  worldFollowing: "Ancré ici",
  worldAddPlanet: "Ajouter en orbite",
  worldRemove: "Retirer",
  worldReset: "Recommencer",
  worldMassLabel: "Masse",
  worldMassHint:
    "D'une lune à une étoile il y a huit ordres de grandeur : le curseur porte l'exposant décimal, pas la masse.",
  worldColumnDistance: "Distance au barycentre",
  worldColumnRadius: "Rayon",
  worldRadiusFloored: "affiché au minimum lisible",
  worldColumnSpeed: "Vitesse",
  worldKindStar: "étoile",
  worldKindPlanet: "planète",
  worldKindProbe: "sonde",
  worldHint:
    "Souris : glisser, molette, clic · Clavier : flèches pour tourner, +/− pour zoomer, Origine pour recadrer",

  // Le tiroir
  worldAnalysesLabel: "Comprendre",
  worldAnalysesOpen: "Ouvrir les analyses",
  worldAnalysesClose: "Fermer",
  worldAnalysesLede:
    "Des expériences pour explorer deux modèles, puis les confronter quand des mesures externes sont disponibles. Ératosthène accepte vos relevés CSV ; les autres expliquent encore pourquoi elles ne peuvent pas comparer.",

  // L'analyse mise en avant — celle qui sait recevoir des mesures
  analysisFeaturedSummary:
    "La seule analyse qui confronte deux modèles à des mesures venues du dehors : chargez vos relevés d'ombre, l'écart s'affiche avec son incertitude. Sans fichier, elle refuse de trancher et dit pourquoi.",
  analysisConfrontsMeasures: "confronte des mesures",
  analysisWithoutMeasures: "aucune mesure importable",
} as const;

export type Messages = typeof fr;
